import { DiscussionType, Message } from "@cnx/common";
import { Box, UnstyledButton } from "@mantine/core";
import { IconChevronsDown } from "@tabler/icons-react";
import { useScrollMovement } from "hooks/useScrollMovement";
import { useScrollToTarget } from "hooks/useScrollToTarget";
import { useEffect, useRef, useState } from "react";
import { groupMessagesByDate } from "utils/chat";
import { useChatScrollContext } from "../ChatScrollContext";
import ChatMessageGroup from "./ChatMessageGroup";

type ChatMessageListProps = {
  messages: Message[];
  discussionType?: DiscussionType;
  firstUnreadMessageId?: string | null;
  scrollMode?: "window" | "container";
  selectedMessageIds?: string[];
  onSelect?: (message: Message) => void;
};

const ChatMessageList = ({
  messages,
  discussionType,
  firstUnreadMessageId,
  scrollMode = "window",
  selectedMessageIds,
  onSelect,
}: ChatMessageListProps) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [newMessagesWhileScrolledUp, setNewMessagesWhileScrolledUp] =
    useState(0);
  const prevMessageCountRef = useRef(messages.length);

  const { bottomRef, containerRef, shouldScroll, setShouldScroll } =
    useChatScrollContext();
  const [bottomVisible, setBottomVisible] = useState(true);
  const { handleScrollDown, showScrollButton: showScrollButtonWindow } =
    useScrollMovement(scrollMode === "container" ? containerRef : undefined);
  const showScrollButton =
    scrollMode === "container" ? !bottomVisible : showScrollButtonWindow;

  useEffect(() => {
    if (scrollMode !== "container" || !bottomRef?.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomVisible(entry.isIntersecting);
        setShouldScroll(entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [scrollMode, bottomRef, setShouldScroll]);

  const groupedMessages = groupMessagesByDate(messages);
  const unreadMessageIndex = messages.findIndex(
    m => m.id === firstUnreadMessageId
  );
  const unreadCount =
    firstUnreadMessageId && unreadMessageIndex >= 0
      ? messages.length - unreadMessageIndex
      : 0;

  useEffect(() => {
    setIsFirstRender(false);
    setShouldScroll(true);
    prevMessageCountRef.current = messages.length;
    requestAnimationFrame(() => {
      bottomRef?.current?.scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    });
  }, []);

  useEffect(() => {
    const threshold = 5;

    const handleScroll = () => {
      const isAtBottom =
        scrollMode === "container"
          ? (containerRef.current?.scrollTop ?? 0) <= threshold
          : window.innerHeight + window.scrollY >=
            document.body.scrollHeight - threshold;

      if (isAtBottom && !shouldScroll) {
        setShouldScroll(true);
        setNewMessagesWhileScrolledUp(0);
        prevMessageCountRef.current = messages.length;
      } else if (!isAtBottom && shouldScroll) {
        setShouldScroll(false);
      }
    };

    if (scrollMode === "container" && containerRef.current) {
      const container = containerRef.current;
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    } else {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [scrollMode, shouldScroll, setShouldScroll, messages.length]);

  useEffect(() => {
    const currentCount = messages.length;
    const prevCount = prevMessageCountRef.current;
    const newCount = currentCount - prevCount;

    if (newCount > 0 && !shouldScroll) {
      setNewMessagesWhileScrolledUp(prev => prev + newCount);
    }

    prevMessageCountRef.current = currentCount;
  }, [messages.length]);

  useScrollToTarget(messages?.[messages.length - 1]?.id, isFirstRender);

  const badgeCount = shouldScroll
    ? 0
    : unreadCount || newMessagesWhileScrolledUp;

  const scrollButtonClass =
    scrollMode === "container"
      ? "h-8 w-8 bg-gray-overlay sticky bottom-4 self-end mr-4 z-50 rounded-full text-white flex items-center justify-center"
      : "h-8 w-8 bg-gray-overlay fixed z-50 bottom-30 right-5 rounded-full text-white flex items-center justify-center";

  const badgeClass =
    scrollMode === "container"
      ? "w-4 h-4 flex items-center justify-center bg-blue absolute z-51 -top-2 -right-2 rounded-full overflow-hidden"
      : "w-4 h-4 flex items-center justify-center bg-blue fixed z-51 bottom-35 right-10 rounded-full overflow-hidden";

  return (
    <Box
      ref={containerRef}
      pb={20}
      pt={10}
      flex={1}
      style={{ scrollPaddingBottom: "300px", minHeight: 0 }}
      className="flex flex-col-reverse overflow-y-auto"
    >
      {showScrollButton && (
        <UnstyledButton
          p={0}
          className={scrollButtonClass}
          style={{
            borderRadius: "50%",
            width: 32,
            height: 32,
            minWidth: 32,
            minHeight: 32,
          }}
          onClick={handleScrollDown}
        >
          {badgeCount > 0 ? (
            <>
              <Box p={0} className={badgeClass}>
                <p className="text-[10px] leading-4 text-center p-0 m-0 w-full h-full flex items-center justify-center">
                  {badgeCount > 9 ? "9+" : badgeCount}
                </p>
              </Box>
              <IconChevronsDown size={24} />
            </>
          ) : (
            <IconChevronsDown size={24} />
          )}
        </UnstyledButton>
      )}
      <Box ref={bottomRef}></Box>

      {[...groupedMessages].reverse().map((group, reverseIndex) => {
        const hasFirstUnreadInThisGroup =
          !!firstUnreadMessageId &&
          group.items.some(message => message.id === firstUnreadMessageId);

        return (
          <ChatMessageGroup
            key={reverseIndex}
            dateKey={group.date}
            items={group.items}
            unReadCount={hasFirstUnreadInThisGroup ? unreadCount : 0}
            discussionType={discussionType}
            firstUnreadMessageId={firstUnreadMessageId}
            selectedMessageIds={selectedMessageIds}
            onSelect={onSelect}
          />
        );
      })}
    </Box>
  );
};
export default ChatMessageList;
